import { useState, useRef } from "react";
import { Upload, X, Camera } from "lucide-react";
import api, { resolveImage } from "@/lib/api";
import { toast } from "sonner";

export default function ImageUpload({ value, onChange, testid = "image-upload" }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large (max 8MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      {value && (
        <div className="relative inline-block">
          <img src={resolveImage(value)} alt="" className="w-32 h-32 object-cover rounded-xl border border-[var(--dsf-border)]" />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 w-6 h-6 bg-[var(--dd-coral)] text-white flex items-center justify-center rounded-full shadow-md"
            data-testid={`${testid}-remove`}
          >
            <X size={12} />
          </button>
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2.5 border border-[var(--dsf-border)] hover:border-[var(--dd-green)] text-sm rounded-full transition-colors disabled:opacity-50"
          data-testid={`${testid}-pick`}
        >
          <Upload size={14} /> {uploading ? "Uploading…" : (value ? "Replace from gallery" : "Choose from gallery")}
        </button>
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2.5 border border-[var(--dsf-border)] hover:border-[var(--dd-green)] text-sm rounded-full transition-colors disabled:opacity-50"
          data-testid={`${testid}-camera`}
        >
          <Camera size={14} /> Take photo
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
      <div className="text-[10px] text-[var(--dsf-text-muted)]">JPG/PNG, max 8MB. Or paste a URL below.</div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://… or /api/files/…"
        className="w-full border border-[var(--dsf-border)] rounded-md px-3 py-2 text-xs"
        data-testid={`${testid}-url`}
      />
    </div>
  );
}
